import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { formatCurrency } from '../../utils/cobroHelpers';
import { holdingFichaPath, noteHoldingId } from '../../utils/holdings';

// [Empresa] · Pedidos · Subtotal · Devueltos · Pago contra entrega
const BREAKDOWN_COLS = 'minmax(0, 1.6fr) minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

const joinParts = (parts) =>
  parts.map((p) => String(p ?? '').trim()).filter(Boolean).join(', ');

/**
 * Receptor y detalle por empresa de la nota CONSOLIDADA de un holding (detalle de la nota,
 * vista del operador). Todo sale de la nota, que es un snapshot al emitir: `receiver` (identidad
 * fiscal del holding) y `companyBreakdown` (pedidos, subtotal, devueltos y COD por empresa). Si
 * el holding cambió de dirección después, la nota conserva la que tenía.
 *
 * El subtotal por empresa es BRUTO: las reglas de la tarifa, los ajustes manuales y el IVA son
 * de la nota entera y no se prorratean.
 *
 * @param {{ note: object }} props
 */
export default function HoldingNoteSummary({ note }) {
  const { t } = useTranslation();
  const cur = note?.currency ?? 'CLP';
  const receiver = note?.receiver ?? {};
  const address = receiver.fiscalAddress ?? {};
  const rows = Array.isArray(note?.companyBreakdown) ? note.companyBreakdown : [];
  const holdingId = noteHoldingId(note);

  const totals = rows.reduce(
    (acc, r) => ({
      packageCount: acc.packageCount + (Number(r.packageCount) || 0),
      subtotal: acc.subtotal + (Number(r.subtotal) || 0),
      returnedCount: acc.returnedCount + (Number(r.returnedCount) || 0),
      returnedAmount: acc.returnedAmount + (Number(r.returnedAmount) || 0),
      codCount: acc.codCount + (Number(r.codCount) || 0),
      codAmount: acc.codAmount + (Number(r.codAmount) || 0),
    }),
    { packageCount: 0, subtotal: 0, returnedCount: 0, returnedAmount: 0, codCount: 0, codAmount: 0 },
  );

  // "3 (−$4.500)": cuántos y cuánto se descuenta (devueltos) o se compensa (COD).
  const countAmountCell = (count, amount) =>
    count > 0 ? `${count} (−${formatCurrency(amount, cur)})` : '—';

  const fiscalLine = joinParts([
    address.street,
    address.municipality,
    address.city && address.city !== address.municipality ? address.city : null,
    address.state,
    address.country,
  ]);

  return (
    <>
      <section className="bill-card bill-card--pad" aria-label={t('Receptor')}>
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
          <h3 className="bill-card__title" style={{ margin: 0 }}>
            {t('Receptor')}
          </h3>
          {holdingId && (
            <a
              href={holdingFichaPath(holdingId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
            >
              {t('Abrir la ficha del holding')}
              <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            </a>
          )}
        </div>
        <dl className="grid gap-3 sm:grid-cols-2" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          <div>
            <dt className="bill-label">{t('Razón social')}</dt>
            <dd className="mb-0">{receiver.legalName || receiver.name || '—'}</dd>
          </div>
          <div>
            <dt className="bill-label">{t('RUT')}</dt>
            <dd className="mb-0">{receiver.businessIdentifier || '—'}</dd>
          </div>
          <div>
            <dt className="bill-label">{t('Giro')}</dt>
            <dd className="mb-0">{receiver.activity || '—'}</dd>
          </div>
          <div>
            <dt className="bill-label">{t('Dirección fiscal')}</dt>
            <dd className="mb-0">{fiscalLine || '—'}</dd>
          </div>
        </dl>
      </section>

      <section className="bill-card bill-card--clip" aria-label={t('Detalle por empresa')}>
        <div className="bill-card__head">
          <h3 className="bill-card__title">
            {t('Detalle por empresa')} <span>({rows.length})</span>
          </h3>
        </div>
        {rows.length === 0 ? (
          <div className="bill-empty" style={{ padding: '1.5rem' }}>
            {t('La nota no trae el detalle por empresa.')}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': BREAKDOWN_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Empresa')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Pedidos')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Subtotal')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Devueltos')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Pago contra entrega')}</span>
            </div>
            {rows.map((r) => (
              <div className="bill-grid__row" role="row" key={String(r.companyId)}>
                <span className="bill-td-strong" role="cell">{r.companyName || String(r.companyId)}</span>
                <span className="bill-grid__cell--end bill-td-muted" role="cell">{r.packageCount ?? 0}</span>
                <span className="bill-grid__cell--end bill-td-amount" role="cell">{formatCurrency(r.subtotal, cur)}</span>
                <span className="bill-grid__cell--end bill-td-muted" role="cell">
                  {countAmountCell(r.returnedCount, r.returnedAmount)}
                </span>
                <span className="bill-grid__cell--end bill-td-muted" role="cell">
                  {countAmountCell(r.codCount, r.codAmount)}
                </span>
              </div>
            ))}
            <div className="bill-grid__row" role="row">
              <span className="bill-td-strong" role="cell">{t('Total')}</span>
              <span className="bill-grid__cell--end bill-td-strong" role="cell">{totals.packageCount}</span>
              <span className="bill-grid__cell--end bill-td-amount" role="cell">{formatCurrency(totals.subtotal, cur)}</span>
              <span className="bill-grid__cell--end bill-td-muted" role="cell">
                {countAmountCell(totals.returnedCount, totals.returnedAmount)}
              </span>
              <span className="bill-grid__cell--end bill-td-muted" role="cell">
                {countAmountCell(totals.codCount, totals.codAmount)}
              </span>
            </div>
          </div>
        )}
        <p className="bill-hint" style={{ padding: '0.5rem 1.25rem 0.75rem', margin: 0 }}>
          {t('El subtotal por empresa es bruto: las reglas de la tarifa, los ajustes y el IVA son de la nota entera.')}
        </p>
      </section>
    </>
  );
}
